<template>
  <div class="page-discussions">
    <div class="page-discussions__header">
      <h2 class="page-discussions__title">
        {{ $t('default.discussions.title') }}
        <span v-if="needsOrgPicker && selectedOrg" class="page-discussions__org-label">
          — {{ selectedOrg.name }}
        </span>
      </h2>

      <div class="page-discussions__controls">
        <OrganizationPicker
          v-if="needsOrgPicker"
          v-model="selectedOrg"
          :label="$t('default.discussions.select_organization')"
          :placeholder="$t('default.discussions.select_organization')"
          class="page-discussions__org-picker"
        />
      </div>
    </div>

    <div v-if="needsOrgPicker && !selectedOrg" class="choose-org-prompt">
      {{ $t('default.discussions.choose_org_to_participate') }}
    </div>
    <template v-else>
      <div v-if="!pageThreadsLoading && pageThreads.length === 0" class="new-thread-composer">
        <PostComposer
          :submitting="creating"
          :error="createError"
          :submit-label="$t('default.discussions.new_thread_submit')"
          @submit="handleCreate"
        />
      </div>

      <div v-if="pageThreadsError" class="error-banner">
        {{ $t('default.discussions.load_error') }}
      </div>

      <div v-else-if="pageThreadsLoading" class="loading-state">
        <Loader />
      </div>

      <div v-else-if="pageThreads.length === 0" class="empty-state">
        <p>{{ $t('default.discussions.empty') }}</p>
      </div>

      <div v-else class="thread-list">
        <div
          v-for="thread in pageThreads"
          :key="thread.id"
          class="thread-block"
        >
          <div v-if="thread.is_locked" class="locked-banner">
            {{ $t('default.discussions.thread_locked') }}
          </div>

          <PageThreadPosts :thread="thread" />
        </div>
      </div>
    </template>

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import OrganizationPicker from '@/components/shared/OrganizationPicker.vue'
import PostComposer from '@/components/discussions/PostComposer.vue'
import PageThreadPosts from '@/components/discussions/PageThreadPosts.vue'
import Loader from '@/components/Loader.vue'
import { usePersistedOrg } from '@/composables/usePersistedOrg'

const props = defineProps({
  page: {
    type: Object,
    required: true,
  },
})

const store = useStore()
const { t } = useI18n()

const { selectedOrg } = usePersistedOrg()
const creating = ref(false)
const createError = ref(null)

const currentUser = computed(() => store.state.user.user)
const userType = computed(() => currentUser.value?.type?.type)
const needsOrgPicker = computed(() =>
  userType.value === 'coach' || userType.value === 'admin'
)

const pageThreads = computed(() => store.state.discussions.pageThreads)
const pageThreadsLoading = computed(() => store.state.discussions.pageThreadsLoading)
const pageThreadsError = computed(() => store.state.discussions.pageThreadsError)

async function handleCreate(body) {
  createError.value = null
  const orgId = needsOrgPicker.value
    ? selectedOrg.value?.id ?? null
    : currentUser.value?.type?.organization_id ?? null

  if (!orgId) {
    createError.value = t('default.discussions.select_organization')
    return
  }

  creating.value = true
  try {
    await store.dispatch('discussions/createThread', {
      body,
      organizationId: orgId,
      pageId: props.page.id,
    })
  } catch (err) {
    const detail = err.response?.data
    createError.value = detail?.detail || detail?.organization?.[0] || detail?.body?.[0] || 'Failed to start discussion.'
  } finally {
    creating.value = false
  }
}

function loadThreads() {
  if (needsOrgPicker.value && !selectedOrg.value) return
  store.dispatch('discussions/fetchPageThreads', {
    pageSlug: props.page.slug,
    organizationId: selectedOrg.value?.id ?? null,
  })
}

watch(selectedOrg, () => {
  store.commit('discussions/setPageThreads', [])
  loadThreads()
})

onMounted(() => {
  loadThreads()
})
</script>

<style scoped>
.page-discussions {
  margin-top: 3rem;
  border-top: 2px solid var(--gray-200);
  padding-top: 2rem;
}

.page-discussions__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.page-discussions__title {
  margin: 0;
}

.page-discussions__org-label {
  font-weight: 400;
  color: var(--text-grey);
}

.page-discussions__controls {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.page-discussions__org-picker {
  min-width: 220px;
}

.choose-org-prompt {
  color: var(--text-grey);
  font-style: italic;
  padding: 1rem 0;
}

.new-thread-composer {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid var(--gray-200);
}

.thread-block {
  margin-bottom: 2rem;
}

.locked-banner {
  background: #fff5f5;
  border: 1px solid var(--halsa-red);
  border-radius: 6px;
  padding: 0.6rem 1rem;
  color: var(--halsa-red);
  margin-bottom: 0.75rem;
  font-weight: 600;
  font-size: 0.9rem;
}

.error-banner {
  background: #fff5f5;
  border: 1px solid var(--halsa-red);
  border-radius: 6px;
  padding: 0.75rem 1rem;
  color: var(--halsa-red);
}

.empty-state {
  text-align: center;
  padding: 2rem 1rem;
  color: var(--text-grey);
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
}
</style>
